// ---------------------------------------------------------------------------
// The letters answer, built from the nine board tiles.
//
// Each tile is consumable exactly once, and tapping and typing go through the
// same state so a physical keyboard and a thumb produce identical results.
//
// The hook also owns the half of the lock rule that lives on the client. A lock
// is a ready signal, not a commitment: the answer that gets submitted is always
// the last thing typed, so any edit after a lock retracts it. `editedSinceLock`
// is that retraction, and it flips on the same commit that changes the word,
// which is a frame earlier than the server's confirmation can arrive. Without
// it a player who locked a short word and then found a longer one would watch
// the screen keep promising the short one.
//
// The answer also carries an insertion point. It is an offset into
// `usedIndices` — into the WORD, that is, not into the board — so it is
// untouched by the shuffle, which permutes display slots and never the answer.
// Everything used to go on and come off the end, so finding that the second
// letter was wrong meant backspacing over everything after it and typing it all
// again inside thirty seconds. Appending is still what happens when the cursor
// is where it starts, at the end, so nothing about the old behaviour moves.
//
// Moving the cursor is deliberately NOT an edit: it changes where the next
// letter lands and not the word, so it streams no draft and retracts no lock.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useRef, useState } from 'react';
import type { LetterTile } from '@countdown/shared';

export interface LettersAnswerApi {
  word: string;
  /** Board indices consumed, in the order they were tapped. */
  usedIndices: number[];
  /** True when this tile index is already spent. */
  isUsed: (index: number) => boolean;
  /**
   * Where the next letter lands, as an offset into the word: 0 is before the
   * first letter, `usedIndices.length` is after the last. Never a board index.
   */
  cursor: number;
  tap: (index: number) => void;
  /** Accepts a typed character; picks the leftmost unused matching tile. */
  typeLetter: (letter: string) => void;
  /** Removes the letter before the cursor. At the start of the word, nothing. */
  backspace: () => void;
  /** Removes the letter after the cursor. At the end of the word, nothing. */
  deleteForward: () => void;
  /** Moves the insertion point by `delta`, clamped to the word. Not an edit. */
  moveCursor: (delta: number) => void;
  clear: () => void;
  reset: () => void;
  /**
   * Rebuilds the board state from a word already on the server, silently.
   *
   * It notifies nobody on purpose: a draft update is exactly what clears a lock
   * server side, so a hook that announced its own rehydration would unlock the
   * player it just restored.
   */
  adopt: (word: string) => void;
  /** An edit has landed since the last lock, so the lock no longer stands. */
  editedSinceLock: boolean;
  /** Call when a lock goes out, to start counting edits again. */
  markLocked: () => void;
}

function wordFrom(tiles: LetterTile[], indices: number[]): string {
  let word = '';
  for (const index of indices) {
    const tile = tiles[index];
    if (tile !== undefined) word += tile.letter;
  }
  return word;
}

/** Leftmost unused tile carrying this letter, or -1. The typing rule, reused. */
function findTile(tiles: LetterTile[], letter: string, taken: number[]): number {
  for (let index = 0; index < tiles.length; index += 1) {
    if (tiles[index].letter.toUpperCase() !== letter) continue;
    if (taken.includes(index)) continue;
    return index;
  }
  return -1;
}

export function useLettersAnswer(
  tiles: LetterTile[],
  onChange: (word: string) => void,
): LettersAnswerApi {
  const [usedIndices, setUsedIndices] = useState<number[]>([]);
  const [cursor, setCursor] = useState(0);
  const [editedSinceLock, setEditedSinceLock] = useState(false);
  // Mirrors the state so the handlers can stay stable and can call onChange
  // themselves, rather than deriving a side effect inside a state updater that
  // React is free to run twice.
  const usedRef = useRef<number[]>([]);
  const cursorRef = useRef(0);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const commit = useCallback(
    (next: number[], nextCursor: number) => {
      usedRef.current = next;
      cursorRef.current = Math.min(Math.max(nextCursor, 0), next.length);
      setUsedIndices(next);
      setCursor(cursorRef.current);
      setEditedSinceLock(true);
      onChangeRef.current(wordFrom(tiles, next));
    },
    [tiles],
  );

  /** The word with one board index spliced in where the cursor is sitting. */
  const insert = useCallback(
    (index: number) => {
      const at = cursorRef.current;
      const used = usedRef.current;
      commit([...used.slice(0, at), index, ...used.slice(at)], at + 1);
    },
    [commit],
  );

  const tap = useCallback(
    (index: number) => {
      if (index < 0 || index >= tiles.length) return;
      if (usedRef.current.includes(index)) return;
      insert(index);
    },
    [insert, tiles.length],
  );

  const typeLetter = useCallback(
    (letter: string) => {
      const wanted = letter.trim().slice(0, 1).toUpperCase();
      if (wanted === '') return;
      // Leftmost unused match, so typing and tapping cannot diverge on a board
      // with duplicate letters. Which tile is picked is a question about the
      // board; where it lands is a question about the word, and the cursor
      // answers only the second one.
      const index = findTile(tiles, wanted, usedRef.current);
      if (index === -1) return;
      insert(index);
    },
    [insert, tiles],
  );

  const backspace = useCallback(() => {
    const at = cursorRef.current;
    if (at === 0) return;
    const used = usedRef.current;
    commit([...used.slice(0, at - 1), ...used.slice(at)], at - 1);
  }, [commit]);

  const deleteForward = useCallback(() => {
    const at = cursorRef.current;
    const used = usedRef.current;
    if (at >= used.length) return;
    // The cursor stays put: the letters after it shuffle left onto it, which is
    // what every text field on the player's device does.
    commit([...used.slice(0, at), ...used.slice(at + 1)], at);
  }, [commit]);

  const moveCursor = useCallback((delta: number) => {
    const next = Math.min(Math.max(cursorRef.current + delta, 0), usedRef.current.length);
    if (next === cursorRef.current) return;
    cursorRef.current = next;
    setCursor(next);
  }, []);

  const clear = useCallback(() => {
    if (usedRef.current.length === 0) return;
    commit([], 0);
  }, [commit]);

  // reset() is clear() under the name that reads right at a round boundary. It
  // notifies too: an emptied answer has to reach the server, or the previous
  // round's draft stays the player's live answer. A round boundary is also the
  // one place where an empty answer is not an edit: there is no lock left to
  // retract.
  const reset = useCallback(() => {
    commit([], 0);
    setEditedSinceLock(false);
  }, [commit]);

  const adopt = useCallback(
    (word: string) => {
      const next: number[] = [];
      for (const letter of word.toUpperCase()) {
        const index = findTile(tiles, letter, next);
        // A word this board cannot build is not this board's word. Leaving the
        // state alone beats showing a partial one that the tiles contradict.
        if (index === -1) return;
        next.push(index);
      }
      usedRef.current = next;
      // After the last letter, where a player carrying on from the server's
      // copy would expect to be typing.
      cursorRef.current = next.length;
      setUsedIndices(next);
      setCursor(next.length);
      setEditedSinceLock(false);
    },
    [tiles],
  );

  const markLocked = useCallback(() => setEditedSinceLock(false), []);

  const isUsed = useCallback((index: number) => usedIndices.includes(index), [usedIndices]);

  return {
    word: wordFrom(tiles, usedIndices),
    usedIndices,
    isUsed,
    cursor,
    tap,
    typeLetter,
    backspace,
    deleteForward,
    moveCursor,
    clear,
    reset,
    adopt,
    editedSinceLock,
    markLocked,
  };
}
